import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StaticPageLayout } from '../layouts/static-page-layout';
import { useAuth } from '../components/auth/AuthProvider';
import { useEffect } from 'react';
import { 
  Sparkles, 
  BookOpen, 
  Target, 
  Star, 
  Clock, 
  User, 
  Calendar, 
  ChevronRight, 
  Search, 
  TrendingUp, 
  Lightbulb, 
  Globe, 
  Atom, 
  Calculator, 
  Microscope, 
  Palette, 
  Book, 
  Users, 
  Shield, 
  PlayCircle, 
  Brain, 
  History, 
  Zap, 
  Award, 
  MapPin, 
  Briefcase, 
  GraduationCap, 
  ArrowRight, 
  CheckCircle,
  Layers,
  Rocket,
  Quote,
  UserCircle
} from 'lucide-react';

export function SparksFeaturePage() {
  const { user, session } = useAuth();
  const isAuthenticated = user && session;

  // Set page title
  useEffect(() => {
    document.title = 'Sparks - Discover Your Academic Journey | AceHorizon';
  }, []);

  const subjectCategories = [
    {
      icon: Calculator,
      title: "STEM Fields",
      description: "Mathematics, Physics, Chemistry, Computer Science, Engineering",
      color: "bg-blue-500",
      gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20",
      examples: ["Quantum Physics", "Machine Learning", "Organic Chemistry", "Calculus"]
    },
    {
      icon: Book,
      title: "Humanities",
      description: "Literature, History, Philosophy, Art, Cultural Studies",
      color: "bg-green-500",
      gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20",
      examples: ["Renaissance Art", "Ancient History", "Modern Literature", "Ethics"]
    },
    {
      icon: Users,
      title: "Social Sciences",
      description: "Psychology, Sociology, Economics, Political Science",
      color: "bg-purple-500",
      gradient: "from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20",
      examples: ["Behavioral Economics", "Social Psychology", "Political Theory", "Anthropology"]
    },
    {
      icon: Globe,
      title: "Applied Sciences",
      description: "Medicine, Business, Law, Environmental Science",
      color: "bg-orange-500",
      gradient: "from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20",
      examples: ["Biomedical Engineering", "Corporate Law", "Climate Science", "Public Health"]
    }
  ];

  const features = [
    {
      icon: History,
      title: "Historical Evolution",
      description: "Trace how subjects evolved from ancient times to modern day, understanding the pivotal moments that shaped each field.",
      highlight: "From Aristotle's Physics to Quantum Mechanics"
    },
    {
      icon: Layers,
      title: "Specialization Pathways", 
      description: "Discover the various specializations within each subject and understand the unique focus areas and career opportunities.",
      highlight: "300+ Specialized Career Tracks"
    },
    {
      icon: Briefcase,
      title: "Career Landscapes",
      description: "Explore real-world applications, job markets, salary expectations, and growth prospects in each field.",
      highlight: "Connect Learning to Your Future"
    },
    {
      icon: Star,
      title: "Notable Personalities",
      description: "Learn from the pioneers, innovators, and thought leaders who shaped each discipline through their groundbreaking contributions.",
      highlight: "Stories of Scientific Giants"
    },
    {
      icon: TrendingUp,
      title: "Future Prospects",
      description: "Understand emerging trends, technological disruptions, and future opportunities in each subject area.",
      highlight: "AI, Climate Tech, Biotech & Beyond"
    },
    {
      icon: Target,
      title: "Purpose & Impact",
      description: "Discover why each subject matters, its real-world applications, and how it contributes to solving global challenges.",
      highlight: "Make Learning Meaningful"
    }
  ];

  const testimonials = [
    {
      quote: "Sparks helped me understand how mathematics connects to everything from music to architecture. I finally found my passion in applied mathematics!",
      author: "Sarah Chen",
      role: "Computer Science Student",
      subject: "Mathematics"
    },
    {
      quote: "The historical timeline feature showed me how psychology evolved from philosophy. It made me appreciate the depth of human understanding.",
      author: "Marcus Thompson",
      role: "Psychology Major",
      subject: "Psychology"
    },
    {
      quote: "I was torn between biology and chemistry. Sparks showed me biochemistry - the perfect intersection of both fields!",
      author: "Priya Patel",
      role: "Pre-Med Student", 
      subject: "Biochemistry"
    }
  ];

  return (
    <StaticPageLayout>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        
        {/* Hero Section */}
        <div className="text-center py-5 sm:py-12">
          <div className="max-w-4xl mx-auto px-2 sm:px-4">
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent leading-tight px-2">
              Sparks
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed px-2">
              Explore the fascinating world of academic subjects through their <em>evolution</em>, <em>specializations</em>, <em>career paths</em>, and <em>future possibilities</em>. 
              From ancient philosophy to quantum computing, discover where your passion lies.
            </p>
          </div>
        </div>

        {/* What is Sparks Section */}
        <div className="from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20 rounded-3xl p-8 md:p-12">
          <div className="text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              What Are Sparks?
            </h2>
            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Sparks are comprehensive explorations of academic subjects that go beyond traditional textbooks. 
              Each Spark illuminates the <strong>why</strong>, <strong>how</strong>, and <strong>where</strong> of learning, 
              helping you understand not just what to study, but why it matters and where it leads.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              {[
                { icon: Lightbulb, title: "Discovery", description: "Uncover the origins and fundamental principles" },
                { icon: Rocket, title: "Innovation", description: "See how fields evolve and create new possibilities" },
                { icon: MapPin, title: "Navigation", description: "Find your path through specialized career landscapes" }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="text-center p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Subject Categories */}
        <div className="space-y-8 mt-16 sm:mt-20">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Explore Academic Universes
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Dive deep into major academic categories and discover the interconnected web of human knowledge
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {subjectCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card key={index} className={`group hover:shadow-2xl transition-all duration-300 border-0 bg-gradient-to-br ${category.gradient} hover:scale-[1.02]`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className={`p-4 rounded-2xl ${category.color} shadow-lg group-hover:shadow-xl transition-shadow`}>
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {category.examples.length} examples
                      </Badge>
                    </div>
                    <CardTitle className="text-2xl font-bold group-hover:text-primary transition-colors">
                      {category.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Popular Areas:</h4>
                      <div className="flex flex-wrap gap-2">
                        {category.examples.map((example, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {example}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Features Grid */}
        <div className="space-y-8 mt-16 sm:mt-20">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Six Dimensions of Learning
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Each Spark explores subjects through multiple lenses, giving you a complete picture of any field
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20 p-3 rounded-lg">
                      <p className="text-sm font-medium text-primary">{feature.highlight}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Student Testimonials */}
        {/* <div className="bg-gray-50 dark:bg-gray-900 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Students Finding Their Spark
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Real stories from learners who discovered their academic passion through Sparks
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <Quote className="h-8 w-8 text-primary mb-4" />
                  <p className="text-gray-700 dark:text-gray-300 mb-6 italic leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <UserCircle className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{testimonial.author}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{testimonial.role}</p>
                      <p className="text-xs text-primary font-medium">{testimonial.subject}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div> */}

        {/* How It Works Section */}
        <div className="space-y-8 mt-16 sm:mt-20">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              How Sparks Work
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Simple, intuitive, and designed to spark curiosity at every step
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { 
                step: "1", 
                icon: Search, 
                title: "Browse & Search", 
                description: "Explore subjects by category or search for specific fields that interest you" 
              },
              { 
                step: "2", 
                icon: BookOpen, 
                title: "Deep Dive", 
                description: "Click on any subject to explore its evolution, specializations, and career paths" 
              },
              { 
                step: "3", 
                icon: Users, 
                title: "Meet Pioneers", 
                description: "Learn about the personalities who shaped each field and their contributions" 
              },
              { 
                step: "4", 
                icon: Rocket, 
                title: "Plan Your Future", 
                description: "Use insights to make informed decisions about your academic and career journey" 
              }
            ].map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-shadow">
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {step.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-3xl p-8 md:p-16 mt-16 sm:mt-20 mb-16 sm:mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose Sparks?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Transform how you think about academic subjects and career possibilities
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Brain,
                title: "Informed Decisions",
                description: "Make better choices about your academic path with comprehensive subject insights"
              },
              {
                icon: Award,
                title: "Career Clarity", 
                description: "Understand job markets, salary expectations, and growth prospects in different fields"
              },
              {
                icon: History,
                title: "Historical Context",
                description: "Appreciate how subjects evolved and connect with the timeline of human knowledge"
              },
              {
                icon: TrendingUp,
                title: "Future-Ready",
                description: "Stay ahead with insights into emerging trends and future opportunities"
              },
              {
                icon: Lightbulb,
                title: "Discover Passion",
                description: "Find subjects that truly resonate with your interests and natural inclinations"
              },
              {
                icon: Target,
                title: "Purpose-Driven",
                description: "Understand the real-world impact and societal value of different fields"
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Final CTA Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-8 text-center mb-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent mb-6">
              Ready to Ignite Your Academic Spark?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto">
              Join thousands of students who have discovered their passion and purpose through Sparks
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              {isAuthenticated ? (
                <Button asChild size="lg" className="px-12 py-4 text-lg">
                  <Link to="/sparks">
                    Explore Sparks Now
                    <Sparkles className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild size="lg" className="px-12 py-4 text-lg">
                    <Link to="/login">
                      Start Your Journey
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Free to get started</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

      </div>
    </StaticPageLayout>
  );
}
